/**
 * Browser Adapter
 * 
 * Gold-standard browser API abstraction layer.
 * Automatically selects best available storage adapter based on context.
 * 
 * Adapter Priority:
 * 1. Native localStorage (best - fast, persistent)
 * 2. Parent Bridge (iframe fallback - persistent via parent)
 * 3. Memory (last resort - session only, shows warning)
 * 
 * @example
 * ```typescript
 * import { browserAdapter } from '~/services/browserAdapter';
 * 
 * // Storage API (always async)
 * await browserAdapter.storage.set('access_token', token);
 * const token = await browserAdapter.storage.get('access_token');
 * 
 * // Check context
 * if (!browserAdapter.context.canPersist) {
 *   butterToast.info('Data will be lost on refresh');
 * }
 * ```
 */

import type {
  BrowserAdapter,
  BrowserStorage,
  BrowserAdapterContext,
  StorageAdapter,
  AdapterType,
  BrowserContext,
  StorageCapabilities,
} from './types';
import { ADAPTER_CONFIG, ADAPTER_NAMES, ERROR_MESSAGES } from './constants';
import {
  detectBrowserContext,
  detectStorageCapabilities,
  logCapabilities,
} from './utils/detector';
import { localStorageAdapter, sessionStorageAdapter } from './adapters/native';
import { parentBridgeAdapter } from './adapters/parentBridge';
import { memoryStorageAdapter } from './adapters/memory';

/**
 * Main browser adapter class
 */
class BrowserAdapterService implements BrowserAdapter {
  private _initialized = false;
  private _activeStorageAdapter: StorageAdapter = memoryStorageAdapter; // localStorage-based
  private _activeSessionAdapter: StorageAdapter = memoryStorageAdapter; // sessionStorage-based
  private _browserContext!: BrowserContext;
  private _storageCapabilities!: StorageCapabilities;

  /**
   * Persistent storage API (uses localStorage with fallbacks)
   */
  readonly storage: BrowserStorage = {
    get: (key: string) => this._activeStorageAdapter.get(key),
    set: (key: string, value: string) => this._activeStorageAdapter.set(key, value),
    remove: (key: string) => this._activeStorageAdapter.remove(key),
    clear: () => this._activeStorageAdapter.clear(),
    keys: () => this._activeStorageAdapter.keys(),
  };

  /**
   * Session storage API (uses sessionStorage with fallbacks)
   */
  readonly session: BrowserStorage = {
    get: (key: string) => this._activeSessionAdapter.get(key),
    set: (key: string, value: string) => this._activeSessionAdapter.set(key, value),
    remove: (key: string) => this._activeSessionAdapter.remove(key),
    clear: () => this._activeSessionAdapter.clear(),
    keys: () => this._activeSessionAdapter.keys(),
  };

  /**
   * Context information
   */
  get context(): BrowserAdapterContext {
    // Auto-initialize if not done yet
    if (!this._initialized) {
      // Run initialization synchronously for first access
      this.initializeSync();
    }

    return {
      browser: this._browserContext,
      capabilities: this._storageCapabilities,
      activeAdapter: this._activeStorageAdapter.name as AdapterType,
      canPersist: this._activeStorageAdapter.name !== 'memory',
      hasOptimalStorage: this._activeStorageAdapter.name === 'native',
    };
  }

  /**
   * Initialize the adapter synchronously
   * 
   * Used for immediate access on first use.
   * Adapter selection happens synchronously.
   */
  private initializeSync(): void {
    if (this._initialized) {
      return;
    }

    // Detect browser context
    this._browserContext = detectBrowserContext();

    // Detect storage capabilities synchronously (basic checks)
    this._storageCapabilities = {
      localStorage: this.testLocalStorage(),
      sessionStorage: this.testSessionStorage(),
      indexedDB: false, // Not checked in sync mode
      parentBridge: this._browserContext.hasIframeResizer && this._browserContext.isIframe,
      cookies: false, // Not checked in sync mode
    };

    // Log capabilities in dev mode
    logCapabilities(this._browserContext, this._storageCapabilities);

    // Select best adapters synchronously
    this.selectAdaptersSync();

    this._initialized = true;
  }

  /**
   * Initialize the adapter asynchronously
   * 
   * Optional: Provides more thorough capability detection.
   * Can be called manually for async initialization.
   */
  async initialize(): Promise<void> {
    if (this._initialized) {
      return;
    }

    // Detect browser context
    this._browserContext = detectBrowserContext();

    // Detect storage capabilities
    this._storageCapabilities = await detectStorageCapabilities();

    // Log capabilities in dev mode
    logCapabilities(this._browserContext, this._storageCapabilities);

    // Select best adapter
    await this.selectAdapters();

    // Show warning if using memory fallback
    if (this._activeStorageAdapter.name === 'memory' || this._activeSessionAdapter.name === 'memory') {
      this.showLimitedStorageWarning();
    }

    this._initialized = true;
  }

  /**
   * Test localStorage availability
   */
  private testLocalStorage(): boolean {
    try {
      const testKey = '__test_localStorage__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Test sessionStorage availability
   */
  private testSessionStorage(): boolean {
    try {
      const testKey = '__test_sessionStorage__';
      sessionStorage.setItem(testKey, 'test');
      sessionStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Select best available storage adapters synchronously
   * Selects both persistent (localStorage) and session (sessionStorage) adapters
   */
  private selectAdaptersSync(): void {
    // SELECT PERSISTENT STORAGE ADAPTER (localStorage-based)
    if (this._storageCapabilities.localStorage) {
      this._activeStorageAdapter = localStorageAdapter;
      if (ADAPTER_CONFIG.debug) {
        // console.log(`[BrowserAdapter] 🎯 Storage: ${ADAPTER_NAMES.native}`);
      }
    } else if (this._storageCapabilities.parentBridge) {
      this._activeStorageAdapter = parentBridgeAdapter;
      if (ADAPTER_CONFIG.debug) {
        // console.log(`[BrowserAdapter] 🎯 Storage: ${ADAPTER_NAMES.parent}`);
      }
    } else {
      this._activeStorageAdapter = memoryStorageAdapter;
      if (ADAPTER_CONFIG.debug) {
        console.warn('[BrowserAdapter] ⚠️ Storage: Using memory fallback (no persistence)');
      }
    }

    // SELECT SESSION STORAGE ADAPTER (sessionStorage-based)
    if (this._storageCapabilities.sessionStorage) {
      this._activeSessionAdapter = sessionStorageAdapter;
      if (ADAPTER_CONFIG.debug) {
        // console.log(`[BrowserAdapter] 🎯 Session: ${ADAPTER_NAMES.session}`);
      }
    } else if (this._storageCapabilities.parentBridge) {
      this._activeSessionAdapter = parentBridgeAdapter;
      if (ADAPTER_CONFIG.debug) {
        // console.log(`[BrowserAdapter] 🎯 Session: ${ADAPTER_NAMES.parent}`);
      }
    } else {
      this._activeSessionAdapter = memoryStorageAdapter;
      if (ADAPTER_CONFIG.debug) {
        console.warn('[BrowserAdapter] ⚠️ Session: Using memory fallback (no persistence)');
      }
    }
  }

  /**
   * Select best available storage adapters asynchronously
   */
  private async selectAdapters(): Promise<void> {
    // Map of adapter instances
    const storageAdapters: Record<string, StorageAdapter> = {
      native: localStorageAdapter,
      parent: parentBridgeAdapter,
      indexedDB: memoryStorageAdapter, // TODO: Implement IndexedDB adapter
      memory: memoryStorageAdapter,
    };

    const sessionAdapters: Record<string, StorageAdapter> = {
      session: sessionStorageAdapter,
      parent: parentBridgeAdapter,
      indexedDB: memoryStorageAdapter, // TODO: Implement IndexedDB adapter
      memory: memoryStorageAdapter,
    };

    // Try storage adapters in priority order (localStorage-based)
    const storagePriority = ['native', 'parent', 'indexedDB', 'memory'];
    for (const type of storagePriority) {
      const adapter = storageAdapters[type];
      
      if (await adapter.isAvailable()) {
        this._activeStorageAdapter = adapter;
        
        if (ADAPTER_CONFIG.debug) {
          // console.log(`[BrowserAdapter] 🎯 Storage: ${ADAPTER_NAMES[adapter.name as AdapterType]}`);
        }
        
        break;
      }
    }

    // Try session adapters in priority order (sessionStorage-based)
    const sessionPriority = ['session', 'parent', 'indexedDB', 'memory'];
    for (const type of sessionPriority) {
      const adapter = sessionAdapters[type];
      
      if (await adapter.isAvailable()) {
        this._activeSessionAdapter = adapter;
        
        if (ADAPTER_CONFIG.debug) {
          // console.log(`[BrowserAdapter] 🎯 Session: ${ADAPTER_NAMES[adapter.name as AdapterType]}`);
        }
        
        break;
      }
    }
  }

  /**
   * Show warning when using memory fallback
   */
  private showLimitedStorageWarning(): void {
    // Only warn in iframe contexts (expected limitation)
    if (!this._browserContext.isIframe) {
      return;
    }

    // Don't spam warnings
    const warningShown = sessionStorage.getItem('__storage_warning_shown__');
    if (warningShown) {
      return;
    }

    console.warn(
      '[BrowserAdapter] ⚠️ Limited Storage Mode',
      `\n${ERROR_MESSAGES.NO_PERSISTENCE}`,
      `\n\nTo enable persistent storage:`,
      `\n1. Add parent bridge script to embedding website`,
      `\n2. Or use standalone site instead of iframe`
    );

    try {
      sessionStorage.setItem('__storage_warning_shown__', 'true');
    } catch {
      // Ignore if sessionStorage also blocked
    }
  }

  /**
   * Get adapter name for debugging
   */
  getAdapterName(): string {
    return ADAPTER_NAMES[this._activeStorageAdapter.name as AdapterType];
  }
}

/**
 * Singleton instance
 */
const browserAdapterInstance = new BrowserAdapterService();

/**
 * Auto-initialize on first access
 */
const browserAdapter: BrowserAdapter = new Proxy(browserAdapterInstance, {
  get(target, prop) {
    // Initialize on first access to storage, session, or context
    if ((prop === 'storage' || prop === 'session' || prop === 'context') && !target['_initialized']) {
      // Initialize asynchronously but return proxy immediately
      target.initialize().catch((error) => {
        console.error('[BrowserAdapter] Failed to initialize:', error);
      });
    }

    return target[prop as keyof typeof target];
  },
});

export { browserAdapter };
